
import { Navbar } from '@/components/Navbar';
import { Helmet } from 'react-helmet-async';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Heart, DollarSign, FileText, Users, Shield, Award } from 'lucide-react';

const NonProfitPEO = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-white to-green-50">
      <Helmet>
        <title>PEO Services for Non-Profit Organizations | The PEO App</title>
        <meta name="description" content="Specialized PEO services for non-profit organizations. Expert grant compliance, volunteer management, and cost-effective HR solutions for charitable organizations." />
        <link rel="canonical" href="https://thepeoapp.com/industries/non-profit" />
        <meta name="keywords" content="non-profit PEO, charity PEO, non-profit HR services, grant compliance, volunteer management, 501c3 PEO, non-profit payroll" />
        
        <meta property="og:title" content="PEO Services for Non-Profit Organizations" />
        <meta property="og:description" content="Specialized PEO solutions for non-profits with grant compliance and volunteer management expertise." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://thepeoapp.com/industries/non-profit" />
      </Helmet>
      
      <Navbar />
      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Heart className="h-16 w-16 text-green-600 mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              PEO Services for Non-Profit Organizations
            </h1>
            <p className="text-xl text-gray-700 leading-relaxed max-w-3xl mx-auto">
              Specialized Professional Employer Organization services for non-profit organizations, 
              charities, and foundations. Cost-effective HR solutions with grant compliance expertise.
            </p>
          </div>

          <div className="bg-white p-8 rounded-xl shadow-lg mb-16">
            <h2 className="text-3xl font-bold mb-6">Non-Profit Organization Challenges</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <DollarSign className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Budget Constraints</h3>
                    <p className="text-gray-700">Limited funding for HR functions and employee benefits</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <FileText className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Grant Compliance</h3>
                    <p className="text-gray-700">Complex reporting requirements and fund allocation tracking</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Users className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Volunteer Management</h3>
                    <p className="text-gray-700">Coordinating volunteers alongside paid staff</p>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Shield className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Regulatory Compliance</h3>
                    <p className="text-gray-700">501(c)(3) regulations and tax-exempt status requirements</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Award className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Mission-Driven Culture</h3>
                    <p className="text-gray-700">Attracting talent with limited compensation budgets</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Heart className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Donor Accountability</h3>
                    <p className="text-gray-700">Transparent use of funds and administrative cost minimization</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-16">
            <h2 className="text-3xl font-bold mb-8 text-center">Non-Profit Specialized PEO Solutions</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-green-50 p-6 rounded-xl">
                <DollarSign className="h-12 w-12 text-green-600 mb-4" />
                <h3 className="text-xl font-semibold mb-3">Cost-Effective HR</h3>
                <p className="text-gray-700">
                  Affordable HR solutions designed for non-profit budgets 
                  while maintaining compliance and quality service.
                </p>
              </div>
              
              <div className="bg-green-50 p-6 rounded-xl">
                <FileText className="h-12 w-12 text-green-600 mb-4" />
                <h3 className="text-xl font-semibold mb-3">Grant Compliance Support</h3>
                <p className="text-gray-700">
                  Expert assistance with grant reporting, fund allocation tracking, 
                  and compliance with grantor requirements.
                </p>
              </div>
              
              <div className="bg-green-50 p-6 rounded-xl">
                <Users className="h-12 w-12 text-green-600 mb-4" />
                <h3 className="text-xl font-semibold mb-3">Volunteer Integration</h3>
                <p className="text-gray-700">
                  Systems to manage both paid staff and volunteers 
                  with appropriate screening and coordination.
                </p>
              </div>
            </div>
          </div>

          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-6">Find Non-Profit Specialized PEOs</h2>
            <p className="text-lg text-gray-700 mb-8">
              Connect with PEO providers who understand non-profit challenges 
              and offer cost-effective solutions designed for charitable organizations.
            </p>
            <Link to="/application">
              <Button className="bg-[#00a86b] hover:bg-[#008f5a] text-white text-lg px-8 py-6 rounded-full">
                Get Non-Profit PEO Quotes
              </Button>
            </Link>
          </div>

          <div className="bg-gray-50 p-8 rounded-xl">
            <h2 className="text-2xl font-bold mb-6">Non-Profit Types We Serve</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-white rounded-lg">
                <h3 className="font-semibold">Social Services</h3>
                <p className="text-sm text-gray-600">Human services, community development</p>
              </div>
              <div className="text-center p-4 bg-white rounded-lg">
                <h3 className="font-semibold">Healthcare</h3>
                <p className="text-sm text-gray-600">Clinics, health centers, research</p>
              </div>
              <div className="text-center p-4 bg-white rounded-lg">
                <h3 className="font-semibold">Education</h3>
                <p className="text-sm text-gray-600">Schools, training programs, literacy</p>
              </div>
              <div className="text-center p-4 bg-white rounded-lg">
                <h3 className="font-semibold">Religious</h3>
                <p className="text-sm text-gray-600">Churches, faith-based organizations</p>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default NonProfitPEO;
